import React, { createContext, useContext, useState, useEffect, ReactNode, useMemo } from 'react';
import { collection, onSnapshot, doc, setDoc } from 'firebase/firestore'; // Firestore functions
import { useData } from '../DataProvider';
import { firestore } from '../../App';
import {
  Parameter,
  IssueFlaggerTable,
  IssueTriageTable,
  StrategyRecommendationTable,
  MorningstarData,
  SurveyData,
  inferResults,
  StrategyRecommendationLeafNode
} from './parameters-schema';

// Default strategy table to use when none is defined
const DEFAULT_STRATEGY_TABLE: StrategyRecommendationTable = {
  id: 'default_strategy_table',
  type: 'strategyRecommendation',
  displayTitle: 'Default Strategy',
  parameterDescription: 'Default strategy when no strategy table is defined',
  inputColumns: [],
  decisionTree: {
    type: 'strategyRecommendationLeaf',
    output: {
      strategyMenu: [],
      explanation: 'No strategy recommendation table has been defined yet.',
      extraTags: []
    }
  } as StrategyRecommendationLeafNode
};

interface ParametersContextProps {
  // Core data
  parameters: Parameter[] | null;
  loadingParameters: boolean;
  
  // Filtered parameter lists
  issueFlaggerTables: IssueFlaggerTable[];
  issueTriageTable: IssueTriageTable | null;
  strategyRecommendationTable: StrategyRecommendationTable | null;
  
  // Parameter management
  updateParameter: (id: string, newParameter: Parameter) => Promise<void>;
  createParameter: (newParameter: Omit<Parameter, 'id'>) => Promise<string>;
  deleteParameter: (id: string) => Promise<void>;
  
  // Inference
  inferResults: (morningstarData: MorningstarData, surveyData: SurveyData) => ReturnType<typeof inferResults> | null;
}

const ParametersContext = createContext<ParametersContextProps | undefined>(undefined);

export const useParameters = (): ParametersContextProps => {
  const context = useContext(ParametersContext);
  if (!context) {
    throw new Error('useParameters must be used within a ParametersProvider');
  }
  return context;
};

interface ParametersProviderProps {
  children: ReactNode;
}

export const ParametersProvider: React.FC<ParametersProviderProps> = ({ children }) => {
  const [parameters, setParameters] = useState<Parameter[] | null>(null);
  const [loadingParameters, setLoadingParameters] = useState(true);
  const { permissions } = useData();

  // Derive filtered parameter lists
  const {
    issueFlaggerTables,
    issueTriageTable,
    strategyRecommendationTable
  } = useMemo(() => {
    if (!parameters) {
      return {
        issueFlaggerTables: [],
        issueTriageTable: null,
        strategyRecommendationTable: null
      };
    }
    
    const issueFlaggerTables = parameters.filter(
      param => param.type === 'issueFlagger'
    ) as IssueFlaggerTable[];
    
    // There will only ever be 0 or 1 triage tables and 0 or 1 strategy tables
    const issueTriageTable = parameters.find(
      param => param.type === 'issueTriage'
    ) as IssueTriageTable | null;
    
    const strategyRecommendationTable = parameters.find(
      param => param.type === 'strategyRecommendation'
    ) as StrategyRecommendationTable | null;
    
    return {
      issueFlaggerTables,
      issueTriageTable,
      strategyRecommendationTable
    };
  }, [parameters]);

  // Set up Firestore subscription
  useEffect(() => {
    const unsubscribe = onSnapshot(collection(firestore, 'parameters'), (snapshot) => {
      try {
        let params: Parameter[] = snapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data(),
        }) as Parameter);

        // Filter out deleted parameters
        params = params.filter(param => !(param as any).deleted);

        setParameters(params);
      } catch (err) {
        console.error('Error processing parameters:', err);
      } finally {
        setLoadingParameters(false);
      }
    }, (err) => {
      console.error('Error fetching parameters:', err);
      setLoadingParameters(false);
    });

    return () => unsubscribe();
  }, []);

  // Parameter management methods
  const updateParameter = async (id: string, newParameter: Parameter) => {
    try {
      if (!newParameter.type) {
        throw new Error('Parameter must have a type');
      }

      const parameterDoc = doc(firestore, 'parameters', id);
      await setDoc(parameterDoc, newParameter);
    } catch (err) {
      console.error('Error updating parameter:', err);
      throw new Error((err as Error).message);
    }
  };

  const createParameter = async (newParameter: Omit<Parameter, 'id'>): Promise<string> => {
    try {
      if (!newParameter.type) {
        throw new Error('Parameter must have a type');
      }
      
      const id = `param_${Date.now()}`;
      const parameterDoc = doc(firestore, 'parameters', id);
      await setDoc(parameterDoc, newParameter);
      return id;
    } catch (err) {
      console.error('Error creating parameter:', err);
      throw new Error((err as Error).message);
    }
  };

  const deleteParameter = async (id: string): Promise<void> => {
    try {
      const parameterDoc = doc(firestore, 'parameters', id);
      await setDoc(parameterDoc, { deleted: true }, { merge: true });
    } catch (err) {
      console.error('Error deleting parameter:', err);
      throw new Error((err as Error).message);
    }
  };

  // Simple wrapper around the inference function
  const infer = (morningstarData: MorningstarData, surveyData: SurveyData) => {
    if (!issueFlaggerTables.length || !issueTriageTable) return null;
    
    // Use the actual strategy table if it exists, otherwise use the default
    const strategyTable = strategyRecommendationTable || DEFAULT_STRATEGY_TABLE;
    
    return inferResults(
      morningstarData,
      surveyData,
      issueFlaggerTables,
      issueTriageTable,
      strategyTable
    );
  };

  // Create the context value
  const contextValue = useMemo(() => ({
    // Core data
    parameters,
    loadingParameters,
    
    // Filtered parameter lists
    issueFlaggerTables,
    issueTriageTable,
    strategyRecommendationTable,
    
    // Parameter management
    updateParameter,
    createParameter,
    deleteParameter,
    
    // Inference
    inferResults: infer
  }), [
    parameters,
    loadingParameters,
    issueFlaggerTables,
    issueTriageTable,
    strategyRecommendationTable
  ]);

  return (
    <ParametersContext.Provider value={contextValue}>
      {children}
    </ParametersContext.Provider>
  );
};
